// client/src/components/doctors/PatientManagement/AddPatientModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import doctorService from '../../../services/doctorService';
import '../../../styles/components/doctor/AddPatientModal.css';

const AddPatientModal = ({ isOpen, onClose, onPatientAdded }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  const searchTimeoutRef = useRef(null);
  const modalRef = useRef(null);

  // Reset modal when closed
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setSelectedPatients([]);
      setError(null);
      setSuccessMessage('');
    }
  }, [isOpen]);

  // Handle search with debouncing
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      performSearch();
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const performSearch = async () => {
    try {
      setSearching(true);
      setError(null);
      
      console.log('🔍 Searching patients with query:', searchQuery);
      
      const response = await doctorService.searchPatients(searchQuery);
      
      if (response.success) {
        setSearchResults(response.data || response.patients || []);
        console.log(`✅ Found ${response.data?.length || 0} patients`);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('❌ Error searching patients:', error);
      setError('Failed to search patients. Please try again.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectPatient = (patient) => {
    const isSelected = selectedPatients.some(p => p._id === patient._id);
    
    if (isSelected) {
      setSelectedPatients(selectedPatients.filter(p => p._id !== patient._id));
    } else {
      setSelectedPatients([...selectedPatients, patient]);
    }
  };

  const handleAddPatients = async () => {
    if (selectedPatients.length === 0) {
      setError('Please select at least one patient');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log(`📋 Adding ${selectedPatients.length} patients to doctor's list`);
      
      // Create associations for each selected patient
      const results = {
        success: [],
        failed: []
      };

      for (const patient of selectedPatients) {
        try {
          // Call the association endpoint
          const response = await doctorService.createPatientAssociation(patient._id);
          
          if (response.success) {
            results.success.push(patient);
          } else {
            results.failed.push({ patient, error: response.message });
          }
        } catch (error) {
          console.error(`Error adding patient ${patient._id}:`, error);
          results.failed.push({ patient, error: error.message });
        }
      }

      // Show results
      if (results.success.length > 0) {
        setSuccessMessage(`Successfully added ${results.success.length} patient(s)`);
        
        // Notify parent component to refresh the list
        if (onPatientAdded) {
          onPatientAdded(results.success);
        }
        
        // Close modal after a short delay
        setTimeout(() => {
          onClose();
        }, 1500);
      }

      if (results.failed.length > 0) {
        const failedNames = results.failed.map(f => 
          `${f.patient.firstName} ${f.patient.lastName}`
        ).join(', ');
        setError(`Failed to add: ${failedNames}`);
      }

    } catch (error) {
      console.error('❌ Error adding patients:', error);
      setError('Failed to add patients. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatPatientInfo = (patient) => {
    const age = patient.age ? `${patient.age} yrs` : 'Age not specified';
    const gender = patient.gender || 'Not specified';
    return `${age}, ${gender}`;
  };

  if (!isOpen) return null;

  return (
    <div className="add-patient-modal-overlay" onClick={onClose}>
      <div 
        className="add-patient-modal-container" 
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="add-patient-modal-header">
          <h2 className="add-patient-modal-title">Add a Patient</h2>
        </div>

        {/* Search Section */}
        <div className="add-patient-search-section">
  <div className="add-patient-search-container">
    <div className="search-icon">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 21L15.5 15.5M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
    <input
      type="text"
      className="add-patient-search-input"
      placeholder="Search patients by name or email..."
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      autoFocus
    />
    {searching && (
      <div className="add-patient-search-spinner">
        <div className="spinner-small"></div>
      </div>
    )}
  </div>
  <div className="add-patient-help-text">
    Type at least 2 characters to start search
  </div>
</div>

        {/* Error/Success Messages */}
        {error && (
          <div className="add-patient-error-message">
            <svg className="error-icon" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {error}
          </div>
        )}

        {successMessage && (
          <div className="add-patient-success-message">
            <svg className="success-icon" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {successMessage}
          </div>
        )}

        {/* Search Results */}
        <div className="add-patient-results-section">
          {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
            <div className="add-patient-no-results">
              <p>No patients found matching "{searchQuery}"</p>
            </div>
          )}

          {searchResults.length > 0 && (
            <>
              <div className="add-patient-results-header">
                <h3>Available Patients ({searchResults.length})</h3>
                <span className="add-patient-selected-count">
                  {selectedPatients.length} selected
                </span>
              </div>

              <div className="add-patient-results-list">
                {searchResults.map((patient) => {
                  const isSelected = selectedPatients.some(p => p._id === patient._id);
                  
                  return (
                    <div
                      key={patient._id}
                      className={`add-patient-result-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectPatient(patient)}
                    >
                      <div className="add-patient-checkbox-wrapper">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="add-patient-checkbox"
                        />
                        <span className="add-patient-checkmark"></span>
                      </div>

                      <div className="add-patient-avatar">
                        {patient.profilePicture ? (
                          <>
                            <img 
                              src={patient.profilePicture} 
                              alt={`${patient.firstName} ${patient.lastName}`}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="add-patient-avatar-fallback" style={{ display: 'none' }}>
                              {patient.firstName?.[0] || 'P'}
                            </div>
                          </>
                        ) : (
                          <div className="add-patient-avatar-fallback">
                            {patient.firstName?.[0] || 'P'}
                          </div>
                        )}
                      </div>

                      <div className="add-patient-info">
                        <h4 className="add-patient-name">
                          {patient.firstName} {patient.lastName}
                        </h4>
                        <p className="add-patient-details">
                          {patient.email}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Selected Patients Summary */}
        {selectedPatients.length > 0 && (
          <div className="add-patient-selected-summary">
            <p>Selected patients will be added to your care list</p>
          </div>
        )}

        {/* Footer Actions */}
        <div className="add-patient-modal-footer">
          <button
            className="add-patient-cancel-btn"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="add-patient-confirm-btn"
            onClick={handleAddPatients}
            disabled={loading || selectedPatients.length === 0}
          >
            {loading ? (
              <>
                <div className="button-spinner"></div>
                Adding...
              </>
            ) : (
              `Add ${selectedPatients.length > 0 ? `(${selectedPatients.length})` : ''} Patient${selectedPatients.length !== 1 ? 's' : ''}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPatientModal;